import React, { useEffect, useLayoutEffect, useState } from 'react';
import classNames from 'clsx';
import { useService } from '@wix/services-manager-react';
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition';
import { SiteScrollBlockerDefinition } from '@wix/viewer-service-site-scroll-blocker/definition';
import { isEmptyObject } from '../../../../../utils/dataUtils';
import Button from '../../../Button/component';
import type {
  HamburgerMenuProps,
  MenuProps,
  MenuStates,
} from '../../Menu.types';
import { selectors, MenuAnimationName } from '../../constants';
import { NavbarMenu } from '../Navbar';
import { useMenuContext } from '../../contexts/MenuContext';
import classes from './Hamburger.module.scss';
import { useFocusTrap } from '../../../../../utils/hooks/useFocusTrap';
import { useAnimationState } from '../MenuContent/MenuItem/useAnimationState';
import {
  ANIMATION_PACKAGE_CONFIG,
  HAMBURGER_MENU_ANIMATION_CONFIG,
  INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
} from '../../animations';
import animationClasses from './HamburgerAnimation.module.scss';

// Predicates that determine when a click inside the hamburger content area should close the menu.
// Add new cases here as new navigable component types are identified.
const HAMBURGER_CONTENT_CLOSE_PREDICATES: Array<
  (event: React.MouseEvent) => boolean
> = [
  // Anchor links: covers buttons, images, rich text, and any other component
  // that renders navigation as an <a> element (the standard ERC pattern).
  event => !!(event.target as HTMLElement).closest('a[href]'),
];

export const HamburgerMenu: React.FC<MenuProps & MenuStates> = ({
  id,
  animationName = MenuAnimationName.None,
  hamburgerMenuOpen: isOpenProp,
  onItemClick,
  onItemDblClick,
  onItemMouseIn,
  onItemMouseOut,
  elementProps: {
    navbar: navbarProps,
    hamburgerOpenButton: hamburgerOpenButtonProps,
    hamburgerMenu: {
      className: hamburgerMenuClassName,
      content,
      headerContent,
      elementProps: {
        hamburgerCloseButton: hamburgerCloseButtonProps,
      } = {} as NonNullable<HamburgerMenuProps['elementProps']>,
    } = {
      isOpen: false,
    },
  },
}) => {
  const { translations, navAriaLabel } = useMenuContext();
  const environment = useService(EnvironmentDefinition);
  const reducedMotion = environment?.reducedMotion ?? false;
  const siteScrollBlocker = useService(SiteScrollBlockerDefinition);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsOpen(!!isOpenProp);
  }, [isOpenProp]);

  // useLayoutEffect ensures the scroll blocker state update (and its
  // fastdom.mutate that removes position:fixed from body) is scheduled
  // BEFORE Thunderbolt's window-level click handler registers its own
  // rAF for anchor scroll calculation. Without this, on iOS with
  // "Reduce Motion" the instant scrollTo fires while the body is still
  // fixed, silently failing and never retrying.
  useLayoutEffect(() => {
    if (isOpen) {
      siteScrollBlocker.setSiteScrollingBlocked(true, id);
    } else {
      siteScrollBlocker.setSiteScrollingBlocked(false, id);
    }
    return () => {
      siteScrollBlocker.setSiteScrollingBlocked(false, id);
    };
  }, [isOpen, siteScrollBlocker, id]);

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(
      HAMBURGER_MENU_ANIMATION_CONFIG,
      INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
    );

  const setIsMenuOpen = React.useCallback(
    (newOpenValue: boolean) => {
      if (newOpenValue === isOpen) {
        return;
      }

      const getHamburgerMenuAnimationName = () => {
        const hamburgerMenuAnimationName =
          ANIMATION_PACKAGE_CONFIG[animationName].hamburgerMenuAnimationName;

        return hamburgerMenuAnimationName;
      };

      const hamburgerMenuAnimationName = getHamburgerMenuAnimationName();
      if (newOpenValue) {
        setIsOpen(true);
        initEnterAnimation(hamburgerMenuAnimationName);
      } else {
        initExitAnimation(hamburgerMenuAnimationName, () => setIsOpen(false));
      }
    },
    [isOpen, setIsOpen, initEnterAnimation, initExitAnimation, animationName],
  );

  const closeMenu = React.useCallback(
    () => setIsMenuOpen(false),
    [setIsMenuOpen],
  );

  const closeMenuForNavigation = React.useCallback(() => {
    if (reducedMotion) {
      setIsOpen(false);
    } else {
      closeMenu();
    }
  }, [closeMenu, reducedMotion]);

  const handleContentClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (HAMBURGER_CONTENT_CLOSE_PREDICATES.some(p => p(event))) {
        closeMenuForNavigation();
      }
    },
    [closeMenuForNavigation],
  );

  const { containerRef: focusTrapRef } = useFocusTrap<HTMLDivElement>(isOpen);

  return (
    <>
      <nav
        aria-label={navAriaLabel || translations.menuNavAriaLabel}
        className={classes.openButtonNavigation}
      >
        <Button
          {...hamburgerOpenButtonProps}
          label=""
          labelContent=""
          a11y={{
            ariaLabel: translations.hamburgerOpenButtonAriaLabel,
            ...hamburgerOpenButtonProps?.a11y,
          }}
          className={classNames(
            selectors.hamburgerMenuOpenButton,
            hamburgerOpenButtonProps?.className,
          )}
          onClick={() => setIsMenuOpen(true)}
        />
      </nav>
      {isOpen && (
        <div
          className={classNames(
            selectors.hamburgerMenu,
            classes.dialog,
            animationClasses.dialog,
            hamburgerMenuClassName,
          )}
          role="dialog"
          aria-modal="true"
          aria-label={navAriaLabel || translations.menuNavAriaLabel}
          data-animation-name={animationState.name}
          data-animation-state={animationState.phase}
          ref={focusTrapRef}
          onKeyDown={e => e.key === 'Escape' && closeMenu()}
        >
          <div className={classNames(classes.dialogContent)}>
            <header className={classes.header}>
              <div
                className={classNames(
                  selectors.hamburgerMenuHeaderContent,
                  classes.headerContent,
                )}
              >
                {headerContent}
              </div>
              <Button
                {...hamburgerCloseButtonProps}
                label=""
                labelContent=""
                a11y={{
                  ariaLabel: translations.hamburgerCloseButtonAriaLabel,
                  ...hamburgerCloseButtonProps?.a11y,
                }}
                className={classNames(
                  selectors.hamburgerMenuCloseButton,
                  classes.closeButton,
                  hamburgerCloseButtonProps?.className,
                )}
                onClick={closeMenu}
              />
            </header>
            <main className={classes.main}>
              <NavbarMenu
                id={id}
                className={classes.navbar}
                {...navbarProps}
                onItemClick={(event, item) => {
                  if (!isEmptyObject(item.link)) {
                    closeMenuForNavigation();
                  }
                  onItemClick?.(event, item);
                }}
                onItemDblClick={onItemDblClick}
                onItemMouseIn={onItemMouseIn}
                onItemMouseOut={onItemMouseOut}
              />
              <div
                className={classNames(
                  selectors.hamburgerMenuContent,
                  classes.content,
                )}
                onClick={handleContentClick}
              >
                {content}
              </div>
            </main>
          </div>
          <div className={classNames(classes.backdrop)} onClick={closeMenu} />
        </div>
      )}
    </>
  );
};
